import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import type { SalonSearchResult } from '../api/use-marketplace';
import { plottableResults, boundsFor, formatDistanceKm } from '../lib/map-helpers';

// Leaflet's default marker icons break under bundlers (rspack) because it
// resolves icon URLs relative to the CSS. Re-point them at the imported assets.
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// A visually distinct "you are here" marker (cognac dot via divIcon).
const userIcon = L.divIcon({
  className: '',
  html:
    '<div style="width:16px;height:16px;border-radius:9999px;background:hsl(20,65%,38%);' +
    'border:3px solid white;box-shadow:0 0 0 2px hsl(20,65%,38%)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

export interface SalonMapProps {
  results: SalonSearchResult[];
  userCoords: { lat: number; lng: number } | null;
}

export function SalonMap({ results, userCoords }: SalonMapProps) {
  const { t } = useTranslation();
  const plottable = plottableResults(results);
  const bounds = boundsFor(results, userCoords);

  if (!bounds) {
    return (
      <div className="rounded-2xl border border-dashed border-border/80 bg-background/50 p-10 md:p-14 text-center">
        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
          {t('salonMap.noMappable')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden border border-border/60 h-full min-h-[420px]">
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [40, 40], maxZoom: 15 }}
        scrollWheelZoom
        className="w-full h-full"
      >
        {/* CARTO Positron — clean light-grey minimal basemap so the salon
            pins stand out (vs. the busier full-colour OSM standard tiles).
            Free, no API key; attribution to OSM + CARTO required. */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={20}
        />
        {userCoords && (
          <Marker position={[userCoords.lat, userCoords.lng]} icon={userIcon}>
            <Popup>{t('search.youAreHere')}</Popup>
          </Marker>
        )}
        {plottable.map((s) => (
          <Marker
            key={s.key}
            position={[s.location!.lat as number, s.location!.lng as number]}
          >
            <Popup>
              <div className="space-y-1">
                <div className="font-semibold">{s.name}</div>
                {formatDistanceKm(s.distanceKm) && (
                  <div className="text-xs text-muted-foreground">
                    {formatDistanceKm(s.distanceKm)}
                  </div>
                )}
                <Link
                  to={`/salon/${s.subdomain}`}
                  className="text-sm text-primary underline underline-offset-2"
                >
                  {t('salonMap.viewSalon')}
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
